import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { leadsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../hooks/useRole';
import {
  Card, Button, Spinner, StageBadge, PriorityBadge, PageHeader, EmptyState, Badge
} from '../../components/ui';
import { LEAD_STAGES, CUSTOMER_TYPES, PRIORITIES, STAGE_COLORS, LEAD_SOURCES } from '../../utils/constants';
import toast from 'react-hot-toast';

const LIMIT = 20;

export default function LeadListPage() {
  const { isAdmin, canViewAllLeads, canAssignLeads, user } = useAuth();
  const { isIntern, isSupport, isAdminOrAbove } = useRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table'); // 'table' | 'kanban'
  const [teamMembers, setTeamMembers] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const fileInputRef = useRef(null);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    stage: searchParams.get('stage') || '',
    customer_type: searchParams.get('customer_type') || '',
    priority: searchParams.get('priority') || '',
    assigned_to: searchParams.get('assigned_to') || '',
    filter: searchParams.get('filter') || '',
  });

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filters.search) params.search = filters.search;
      if (filters.stage) params.stage = filters.stage;
      if (filters.customer_type) params.customer_type = filters.customer_type;
      if (filters.priority) params.priority = filters.priority;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      const res = await leadsAPI.getAll(params);
      setLeads(res.data?.data?.leads || []);
      setTotal(res.data?.data?.pagination?.total || 0);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [page, filters, refreshKey]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    if (canAssignLeads) {
      usersAPI.getAll().then(r => setTeamMembers(r.data?.data?.users || [])).catch(() => {});
    }
  }, [canAssignLeads]);

  const setFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(1);
  };

  const handleExport = async () => {
    try {
      const res = await leadsAPI.exportCSV();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const handleDownloadTemplate = () => {
    // All columns supported by the import — mirrors the Create Lead form exactly.
    // Type-specific columns apply only to the relevant customer_type; leave blank for others.
    const headers = [
      // ── Basic Information ──────────────────────────────────────────────────────
      'customer_type',       // Required. One of: Private University | Autonomous College | Regular College | Open University | Other Educational Institute | Franchise | Master Franchise | Corporate | Direct Student | Other
      'stage',               // Optional. One of: New | Contacted | Interested | Proposal Sent | Negotiation | Partnership Closed | Lost  (default: New)
      'priority',            // Optional. High | Medium | Low  (default: Medium)
      'source',              // Optional. cold_call | referral | LinkedIn | email_campaign | walk_in | web_form | RCM
      'region',              // Optional. Indian state name (e.g. Maharashtra)
      'state',               // Optional. Same as region (alternative column name)
      // ── Contact Information ────────────────────────────────────────────────────
      'contact_name',        // Contact person full name
      'contact_phone',       // Required for duplicate detection (10-digit mobile)
      'contact_email',       // Optional
      // ── Notes ─────────────────────────────────────────────────────────────────
      'notes',               // General notes / context
      'internal_notes',      // Internal team notes
      'deal_value',          // Optional numeric deal value (₹)
      // ── University / College Details (customer_type: Private University, Autonomous College, Regular College, Open University, Other Educational Institute) ──
      'institution_name',    // Full official institution name
      'website',             // Institution website URL
      'city',                // City where institution is located
      'annual_intake',       // Annual student intake number
      'has_ai_programs',     // yes | no
      'decision_maker_name', // Name of decision maker
      'decision_maker_designation', // Designation of decision maker
      'mou_status',          // not_started | draft_sent | under_review | signed
      // ── Franchise / Master Franchise Details ──────────────────────────────────
      // 'name' column = Applicant Name for Franchise; also used as Student Name for Direct Student; Name/Org for Other
      'name',                // Applicant name (Franchise) | Student name (Direct Student) | Name/Org (Other)
      // city column is shared (already listed above)
      'investment_capacity', // Investment capacity in ₹ (Franchise)
      'prior_experience',    // Prior education/business experience (Franchise)
      'infrastructure_ready',// yes | no | planned (Franchise)
      'target_launch',       // Target launch date YYYY-MM-DD (Franchise)
      // ── Corporate Details ─────────────────────────────────────────────────────
      'company_name',        // Company name (Corporate)
      'industry',            // Industry sector (Corporate)
      'employees_to_train',  // Number of employees to train (Corporate)
      'training_mode',       // online | offline | hybrid (Corporate)
      'budget',              // Training budget in ₹ (Corporate)
      'hr_contact',          // HR contact name (Corporate)
      // ── Direct Student Details ────────────────────────────────────────────────
      // 'name' column = Student name (already listed above)
      'dob',                 // Date of birth YYYY-MM-DD (Direct Student)
      'qualification',       // Highest qualification (Direct Student)
      'occupation',          // Current occupation (Direct Student)
      'preferred_course',    // Preferred course (Direct Student)
      'placement_required',  // yes | no (Direct Student)
      'test_score',          // Assessment test score 0–100 (Direct Student; ≥60 = scholarship eligible)
      // ── Other Details ─────────────────────────────────────────────────────────
      'details',             // Additional details (Other customer type)
    ];

    // Example rows — one per common customer type
    const escCsv = v => `"${String(v).replace(/"/g, '""')}"`;
    const blankRow = (overrides) => headers.map(h => escCsv(overrides[h] !== undefined ? overrides[h] : '')).join(',');

    const rows = [
      headers.map(h => escCsv(h)).join(','),

      // Private University example
      blankRow({
        customer_type: 'Private University', stage: 'New', priority: 'High', source: 'LinkedIn',
        region: 'Maharashtra', contact_name: 'Dr. Raj Kumar', contact_phone: '9876543210',
        contact_email: 'raj@university.edu', notes: 'Met at education expo',
        institution_name: 'Example University', website: 'https://example.edu',
        city: 'Pune', annual_intake: '2000', has_ai_programs: 'no',
        decision_maker_name: 'Dr. Raj Kumar', decision_maker_designation: 'Vice Chancellor',
        mou_status: 'not_started',
      }),

      // Franchise example
      blankRow({
        customer_type: 'Franchise', stage: 'Contacted', priority: 'Medium', source: 'referral',
        region: 'Karnataka', contact_name: 'John Doe', contact_phone: '9876543211',
        contact_email: 'john@franchise.com',
        name: 'John Doe', city: 'Bangalore',
        investment_capacity: '500000', prior_experience: '5 years in education',
        infrastructure_ready: 'yes', target_launch: '2026-06-01',
      }),

      // Corporate example
      blankRow({
        customer_type: 'Corporate', stage: 'Interested', priority: 'High', source: 'cold_call',
        region: 'Delhi', contact_name: 'Priya Singh', contact_phone: '9876543212',
        contact_email: 'priya@corp.com',
        company_name: 'TechCorp India', industry: 'IT Services',
        employees_to_train: '150', training_mode: 'hybrid',
        budget: '750000', hr_contact: 'Priya Singh',
      }),

      // Direct Student example
      blankRow({
        customer_type: 'Direct Student', stage: 'New', priority: 'Medium', source: 'web_form',
        region: 'Tamil Nadu', contact_name: 'Ananya Raj', contact_phone: '9876543213',
        contact_email: 'ananya@email.com',
        name: 'Ananya Raj', dob: '2002-05-15',
        qualification: 'B.Tech', occupation: 'Fresher',
        preferred_course: 'AI & ML', placement_required: 'yes', test_score: '72',
      }),
    ];

    const csvContent = rows.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads-import-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await leadsAPI.importCSV(form);
      const { imported = 0, failed = 0, errors: rowErrors = [] } = res.data?.data || {};

      setImportErrors(rowErrors);

      if (imported === 0 && failed === 0) {
        toast.error('No data rows found in the file');
      } else if (imported === 0) {
        const firstErr = rowErrors[0];
        toast.error(
          `Import failed: ${firstErr ? `Row ${firstErr.row}: ${firstErr.error}` : `${failed} row(s) had errors`}`,
          { duration: 6000 }
        );
      } else if (failed > 0) {
        toast(`Imported ${imported} lead(s). ${failed} row(s) skipped — see details below.`, { icon: '⚠️', duration: 6000 });
      } else {
        toast.success(`Successfully imported ${imported} lead(s)`);
        setImportErrors([]);
      }

      if (imported > 0) {
        // Reset to page 1 and force a fresh fetch so imported leads are always visible
        setPage(1);
        setRefreshKey(k => k + 1);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleStageMove = async (leadId, newStage) => {
    try {
      await leadsAPI.update(leadId, { stage: newStage });
      setLeads(ls => ls.map(l => l._id === leadId ? { ...l, stage: newStage } : l));
    } catch {
      toast.error('Failed to move lead');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <PageHeader
        title="Leads"
        subtitle={`${total} total lead${total !== 1 ? 's' : ''}`}
        actions={
          <>
            {isAdmin && (
              <>
                <Button variant="ghost" size="sm" disabled={importing} onClick={() => !importing && fileInputRef.current?.click()}>
                  {importing ? '⏳ Importing…' : '📥 Import CSV'}
                </Button>
                <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImport} />
                <Button variant="ghost" size="sm" onClick={handleDownloadTemplate}
                  title={`Download a ready-to-fill CSV template with example rows.\n\nColumns:\n• customer_type (REQUIRED): ${CUSTOMER_TYPES.join(', ')}\n• source: ${LEAD_SOURCES.join(', ')}\n• priority: High, Medium, Low\n• region, state, city, contact_name, contact_phone, contact_email, notes (all optional)`}>
                  📋 Sample CSV
                </Button>
                <Button variant="ghost" size="sm" onClick={handleExport}>📤 Export</Button>
              </>
            )}
            <Button variant="gold" size="sm" onClick={() => navigate('/leads/new')}>+ New Lead</Button>
          </>
        }
      />

      {/* Import error details */}
      {importErrors.length > 0 && (
        <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 10, padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#c2410c' }}>⚠️ {importErrors.length} row(s) skipped during import</span>
            <button onClick={() => setImportErrors([])} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {importErrors.map((e, idx) => (
              <div key={idx} style={{ fontSize: 12, color: '#7c3aed', background: '#f5f3ff', borderRadius: 6, padding: '4px 10px' }}>
                <strong>Row {e.row}:</strong> {e.error}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search name, phone, email, lead ID…"
            value={filters.search}
            onChange={e => setFilter('search', e.target.value)}
            style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 220 }}
          />
          <select value={filters.stage} onChange={e => setFilter('stage', e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff' }}>
            <option value="">All Stages</option>
            {LEAD_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.customer_type} onChange={e => setFilter('customer_type', e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff' }}>
            <option value="">All Types</option>
            {CUSTOMER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filters.priority} onChange={e => setFilter('priority', e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff' }}>
            <option value="">All Priorities</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {canAssignLeads && (
            <select value={filters.assigned_to} onChange={e => setFilter('assigned_to', e.target.value)}
              style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff' }}>
              <option value="">All Assignees</option>
              {teamMembers.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
            </select>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            <button onClick={() => setView('table')} style={{ padding: '6px 12px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: view === 'table' ? '#1B2A6B' : '#fff', color: view === 'table' ? '#fff' : '#64748b', cursor: 'pointer', fontSize: 13 }}>
              ☰ Table
            </button>
            <button onClick={() => setView('kanban')} style={{ padding: '6px 12px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: view === 'kanban' ? '#1B2A6B' : '#fff', color: view === 'kanban' ? '#fff' : '#64748b', cursor: 'pointer', fontSize: 13 }}>
              ⊞ Kanban
            </button>
          </div>
        </div>
      </Card>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={36} /></div>
      ) : leads.length === 0 ? (
        <EmptyState icon="🎯" title="No leads found" subtitle="Create your first lead or adjust filters" />
      ) : view === 'table' ? (
        <TableView leads={leads} onNavigate={id => navigate(`/leads/${id}`)} />
      ) : (
        <KanbanView leads={leads} onMove={handleStageMove} onNavigate={id => navigate(`/leads/${id}`)} />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
          <span style={{ fontSize: 13, color: '#64748b' }}>Page {page} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</Button>
        </div>
      )}
    </div>
  );
}

function TableView({ leads, onNavigate }) {
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
              {['Lead ID', 'Name / Org', 'Type', 'Stage', 'Priority', 'Assigned To', 'Source', 'Created'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map(lead => (
              <tr key={lead._id} onClick={() => onNavigate(lead._id)}
                style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '11px 14px', fontSize: 12, color: '#1B2A6B', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {lead.lead_id}
                  {lead.is_stale && <span style={{ marginLeft: 6, fontSize: 10, background: '#fef3c7', color: '#d97706', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>STALE</span>}
                </td>
                <td style={{ padding: '11px 14px' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{lead.contact_name || lead.type_specific_data?.name || '—'}</p>
                  <p style={{ fontSize: 11, color: '#94a3b8' }}>{lead.type_specific_data?.institution_name || lead.type_specific_data?.company_name || lead.contact_email || ''}</p>
                </td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: '#475569' }}>{lead.customer_type}</td>
                <td style={{ padding: '11px 14px' }}><StageBadge stage={lead.stage} /></td>
                <td style={{ padding: '11px 14px' }}><PriorityBadge priority={lead.priority} /></td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: '#475569' }}>{lead.assigned_to?.name || <span style={{ color: '#cbd5e1' }}>Unassigned</span>}</td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: '#64748b', textTransform: 'capitalize' }}>{lead.source?.replace('_', ' ')}</td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(lead.createdAt).toLocaleDateString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function KanbanView({ leads, onMove, onNavigate }) {
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const byStage = LEAD_STAGES.reduce((acc, s) => {
    acc[s] = leads.filter(l => l.stage === s);
    return acc;
  }, {});

  const handleDrop = (stage) => {
    if (dragging && dragging.stage !== stage) {
      onMove(dragging._id, stage);
    }
    setDragging(null);
    setDragOver(null);
  };

  return (
    <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
      {LEAD_STAGES.map(stage => {
        const c = STAGE_COLORS[stage] || { bg: '#f1f5f9', text: '#64748b' };
        const col = byStage[stage] || [];
        return (
          <div key={stage}
            onDragOver={e => { e.preventDefault(); setDragOver(stage); }}
            onDrop={() => handleDrop(stage)}
            onDragLeave={() => setDragOver(null)}
            style={{ minWidth: 220, flex: '0 0 220px', background: dragOver === stage ? `${c.text}15` : '#f8fafc', borderRadius: 10, border: `2px solid ${dragOver === stage ? c.text : '#e2e8f0'}`, transition: 'all 0.2s' }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: c.text }}>{stage}</span>
              <span style={{ background: c.bg, color: c.text, padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{col.length}</span>
            </div>
            <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 60 }}>
              {col.map(lead => (
                <div key={lead._id}
                  draggable
                  onDragStart={() => setDragging(lead)}
                  onClick={() => onNavigate(lead._id)}
                  style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid #e2e8f0', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', userSelect: 'none' }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#1B2A6B', marginBottom: 3 }}>{lead.lead_id}</p>
                  <p style={{ fontSize: 12, color: '#334155', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lead.contact_name || lead.type_specific_data?.name || lead.type_specific_data?.institution_name || '—'}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>{lead.customer_type}</span>
                    <PriorityBadge priority={lead.priority} />
                  </div>
                  {lead.assigned_to && (
                    <p style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>👤 {lead.assigned_to.name}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
